import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { cn } from '@divlab/divanui';
import { IconZoomInSingle, IconZoomOutSingle } from '@divlab/divanui/icons';

import Cylindo360Viewer from '@Components/Cylindo360Viewer';
import CylindoRotateHint from '@Components/CylindoRotateHint';
import styles from './ProductModel.module.css';

import type { Cylindo360ViewerOpts } from '@Types/Cylindo360Viewer';
import type { FC, HTMLAttributes } from 'react';

export interface ProductModelProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  medias: Cylindo360ViewerOpts;
  onViewerReady?: () => void;
  onError?: () => void;
  onFeaturesError?: () => void;
  view?: 'updated';
}

const ProductModel: FC<ProductModelProps> = (props) => {
  const { className, medias, view, onViewerReady, onError, onFeaturesError, ...restProps } = props;
  const [zoom, setZoom] = useState<[number, number]>(null);
  const [draggable, setDraggable] = useState(false);
  const timerId = useRef(null);

  const handleZoomEnter = useCallback(({ x, y }) => {
    setZoom([x, y]);
  }, []);

  const handleZoomExit = useCallback(() => {
    setZoom(null);
  }, []);

  const handleZoom = useCallback(() => {
    setZoom((prev) => (prev ? null : [0.5, 0.5]));
  }, []);

  const handleMouseDown = () => {
    setDraggable(true);

    clearTimeout(timerId.current);
  };

  const handleMouseUp = useCallback(() => {
    if (!draggable) return;

    timerId.current = setTimeout(() => {
      setDraggable(false);
    }, 6000);
  }, [draggable]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      clearTimeout(timerId.current);
    };
  }, [handleMouseUp]);

  return (
    <div
      {...restProps}
      className={cn(
        styles.productModel,
        {
          [styles.zoomed]: !!zoom,
          [styles.viewUpdated]: view === 'updated',
          [styles.draggable]: draggable,
        },
        className,
      )}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
    >
      <div className={styles.blockWrapper}>
        <div className={styles.actions}>
          {zoom ? (
            <div className={styles.action}>
              <IconZoomOutSingle onClick={handleZoom} />
            </div>
          ) : (
            <div className={styles.action}>
              <IconZoomInSingle onClick={handleZoom} />
            </div>
          )}
        </div>

        <div className={styles.content}>
          <div
            className={cn(styles.wrapperCylindo, {
              [styles.zoomed]: !!zoom,
            })}
          >
            <Cylindo360Viewer
              className={styles.cylindoViewer}
              opts={{
                ...medias,
                backgroundColor: 'F5F3F1',
              }}
              zoom={zoom}
              onZoomEnter={handleZoomEnter}
              onZoomExit={handleZoomExit}
              onViewerReady={onViewerReady}
              onError={onError}
              onFeaturesError={onFeaturesError}
            />

            <div className={styles.wrapperRotateHint}>
              <CylindoRotateHint size='m' view={view} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ProductModel);
